import type { AmbianceAsset } from '@matter_extractor/shared';
import { type InUse, MEDIATHEQUE_EMPTY_TESTID, MEDIATHEQUE_GRID_TESTID } from './contracts';
import { MediathequeCard } from './mediatheque-card';

/**
 * The grid of the Médiathèque (issue #121): NOT built on `components/admin/
 * versioned/` - a file carries no history, no production pointer and no
 * "used in" in the sense §4 gives those words, so mounting the versioned
 * layer over it would draw three empty panels nobody can open.
 *
 * The empty state is a real sentence and not a blank grid (H7): the library
 * legitimately restarts empty after the coupe, and a blank rectangle under a
 * filter bar reads as broken rather than as "not built yet".
 */

export interface MediathequeGridProps {
  assets: readonly AmbianceAsset[];
  /** Why the grid is empty, worded by the caller - only it knows if a filter is armed. */
  emptyMessage: string;
  confirmingId: string | null;
  /**
   * The id of the ONE card whose deletion is in flight, or null. Derived by
   * the caller from `removeAsset.isPending`/`.variables` rather than tracked
   * as a second piece of state, so "is a delete running, and for which id" has
   * a single source of truth (issue #121 review).
   */
  deletingId: string | null;
  /** The referents of the ONE card a 409 `ASSET_IN_USE` last targeted. */
  inUse: InUse | null;
  onRequestDelete: (asset: AmbianceAsset) => void;
  onCancelDelete: () => void;
  onConfirmDelete: (asset: AmbianceAsset) => void;
  onDismissReferents: () => void;
}

export function MediathequeGrid({
  assets,
  emptyMessage,
  confirmingId,
  deletingId,
  inUse,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
  onDismissReferents,
}: MediathequeGridProps) {
  if (assets.length === 0) {
    return (
      <p data-testid={MEDIATHEQUE_EMPTY_TESTID} className="text-gray-600 text-sm">
        {emptyMessage}
      </p>
    );
  }

  return (
    <ul
      data-testid={MEDIATHEQUE_GRID_TESTID}
      className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-4"
    >
      {assets.map((asset) => (
        <MediathequeCard
          key={asset.id}
          asset={asset}
          confirming={confirmingId === asset.id}
          deleting={deletingId === asset.id}
          // Another card's delete is in flight: this one's "Supprimer" stays
          // closed rather than letting a second request overwrite the shared
          // mutation's variables mid-flight (issue #121 review).
          disableDelete={deletingId !== null && deletingId !== asset.id}
          referents={inUse !== null && inUse.assetId === asset.id ? inUse.referents : null}
          onRequestDelete={() => onRequestDelete(asset)}
          onCancelDelete={onCancelDelete}
          onConfirmDelete={() => onConfirmDelete(asset)}
          onDismissReferents={onDismissReferents}
        />
      ))}
    </ul>
  );
}
